import { subscribeDesktopRealtime, subscribeDesktopWaveformUpdates } from "./desktop-event-gateway";
import {
  ParsedAudioEvent,
  ParsedDesktopEvent,
  ParsedTranscriptEvent,
} from "./desktop-event-parser";
import { decodeBase64Pcm16 } from "./desktop-audio-assembler";

import { electronModule } from "@/modules/electron";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { Uuid } from "@/domains/global/identifiers";
import { PreparedRecording } from "@/store/audio/recording/prepared-recording-controller";
import pTimeout from "p-timeout";
import type { DesktopWaveformUpdate } from "@/modules/audio-recording/desktop-waveform-update";
import { DESKTOP_PCM_SAMPLE_RATE } from "./desktop-pcm-persistence";
import type {
  DesktopRecorderTranscriptFinalTurn,
  DesktopRecorderTranscriptPartialTurn,
  DesktopRecorderTranscriptTurn,
} from "@/store/audio/recording/media-transcriber/transcriber.types";
import {
  DesktopAudioRecordingStrategy,
  DesktopRecordingStartAttemptTrace,
  DesktopRecordingStartFailurePhase,
  DesktopRecordingStartFailureSource,
  StartRecordingOutcome,
  StartRecordingOutcomeKind,
} from "@/modules/electron/types";
import {
  DesktopRecordingStartFatalError,
  DesktopRecordingRestartRequiredError,
  buildDesktopRecordingStartAttemptLogInfo,
} from "@/store/audio/recording/media-controller/desktop-controller-errors";

const STOP_TIMEOUT_MS = 10_000;
const STOP_TIMEOUT_SENTINEL = Symbol("desktop-capture-stop-timeout");

export type DesktopCaptureCallbacks = {
  onTranscriptPartial: (args: {
    channel: 0 | 1;
    partialTurn: DesktopRecorderTranscriptPartialTurn;
  }) => void;
  onTranscriptFinal: (args: {
    channel: 0 | 1;
    finalTurn: DesktopRecorderTranscriptFinalTurn;
  }) => void;
  onAudioSamples?: (args: { samples: Int16Array }) => void;
  onWaveformUpdate?: (args: { update: DesktopWaveformUpdate }) => void;
  onDebugEvent?: (args: { data: Record<string, unknown>; kind: string }) => void;
};

type SessionActionArgs = {
  windowId?: string | null;
  audioRecordingId?: Uuid | null;
};

export class DesktopCaptureService {
  private windowId: string | null = null;
  private detectedMeetingWindowId: string | null = null;
  private uploadToken: string | null = null;
  private unsubscribe: (() => void) | null = null;
  private waveformUnsubscribe: (() => void) | null = null;
  private nextTranscriptTurnOrder = 0;
  private readonly activeTranscriptTurnOrdersBySpeaker = new Map<string, number>();
  private readonly lastFinalTranscriptTimestampBySpeaker = new Map<string, number>();
  private stoppingPromise: Promise<void> | null = null;
  private activeWindowId: string | null = null;
  private calendarEventId?: string;
  private preparedRecording: PreparedRecording | null;
  private audioRecordingId: Uuid | null = null;
  private recordingStrategy: DesktopAudioRecordingStrategy =
    DesktopAudioRecordingStrategy.WebArtifact;
  private isAttachedToRecoveredSession = false;
  private startAttemptTrace: DesktopRecordingStartAttemptTrace | null = null;
  private windowIdResolved = false;
  private uploadTokenResolved = false;

  private callbacks: DesktopCaptureCallbacks;

  constructor(preparedRecording: PreparedRecording | null, callbacks: DesktopCaptureCallbacks) {
    this.preparedRecording = preparedRecording;
    this.callbacks = callbacks;
  }

  isRecordingWindowId(windowId?: string): boolean {
    return this.detectedMeetingWindowId === windowId;
  }

  getPreparationProgress(): {
    windowIdResolved: boolean;
    uploadTokenResolved: boolean;
  } {
    return {
      windowIdResolved: this.windowIdResolved,
      uploadTokenResolved: this.uploadTokenResolved,
    };
  }

  async prepare({
    associatedNoteId,
    calendarEventId,
    associatedAudioRecordingId,
    detectedMeetingWindowId,
    recordingStrategy,
    startAttemptTrace,
    abortSignal,
  }: {
    associatedAudioRecordingId: Uuid;
    associatedNoteId: Uuid | null;
    detectedMeetingWindowId?: string;
    calendarEventId?: string;
    recordingStrategy: DesktopAudioRecordingStrategy;
    startAttemptTrace?: DesktopRecordingStartAttemptTrace;
    customVocabulary?: string[];
    abortSignal?: AbortSignal;
  }): Promise<void> {
    this.resetTranscriptState();
    this.windowIdResolved = false;
    this.uploadTokenResolved = false;
    if (!this.preparedRecording) {
      throw new Error("Desktop capture was not prepared");
    }
    const prepareStartedAt = Date.now();
    let windowIdResolved = false;
    let uploadTokenResolved = false;

    try {
      const windowId = await this.preparedRecording.windowId;
      windowIdResolved = true;
      this.windowIdResolved = true;
      if (abortSignal?.aborted) return;

      this.windowId = windowId;
      this.detectedMeetingWindowId = detectedMeetingWindowId ?? null;
      this.activeWindowId = windowId;
      this.calendarEventId = calendarEventId;
      this.audioRecordingId = associatedAudioRecordingId;
      this.recordingStrategy = recordingStrategy;
      this.isAttachedToRecoveredSession = false;
      this.startAttemptTrace = startAttemptTrace ?? null;

      const recallUploadCredentials = await this.preparedRecording.recallUploadCredentials;
      uploadTokenResolved = Boolean(recallUploadCredentials.uploadToken);
      this.uploadTokenResolved = uploadTokenResolved;
      this.uploadToken = recallUploadCredentials.uploadToken;
      if (abortSignal?.aborted) return;

      logger.info({
        message: "[DesktopCaptureService] prepared",
        info: {
          audioRecordingId: associatedAudioRecordingId,
          associatedNoteId,
          detectedMeetingWindowId: detectedMeetingWindowId ?? null,
          recordingStrategy,
          windowId,
          windowIdResolved,
          uploadTokenResolved,
          prepareTotalMs: Date.now() - prepareStartedAt,
          ...buildDesktopRecordingStartAttemptLogInfo({
            startAttemptTrace,
          }),
        },
      });

      this.subscribeToDesktopEvents({ associatedAudioRecordingId });
    } catch (error) {
      logger.warn({
        message: "[DesktopCaptureService] prepare failed",
        info: {
          audioRecordingId: associatedAudioRecordingId,
          associatedNoteId,
          detectedMeetingWindowId: detectedMeetingWindowId ?? null,
          recordingStrategy,
          failureSource: DesktopRecordingStartFailureSource.RendererPrepare,
          failurePhase: windowIdResolved
            ? DesktopRecordingStartFailurePhase.UploadCredentials
            : DesktopRecordingStartFailurePhase.PrepareWindow,
          windowIdResolved,
          uploadTokenResolved,
          prepareTotalMs: Date.now() - prepareStartedAt,
          error: error instanceof Error ? error.message : String(error),
          ...buildDesktopRecordingStartAttemptLogInfo({
            startAttemptTrace,
          }),
        },
      });
      throw error;
    }
  }

  async attach({
    associatedAudioRecordingId,
    recordingStrategy,
  }: {
    associatedAudioRecordingId: Uuid;
    recordingStrategy: DesktopAudioRecordingStrategy;
  }): Promise<void> {
    this.resetTranscriptState();
    this.windowId = null;
    this.detectedMeetingWindowId = null;
    this.activeWindowId = null;
    this.calendarEventId = undefined;
    this.uploadToken = null;
    this.audioRecordingId = associatedAudioRecordingId;
    this.recordingStrategy = recordingStrategy;
    this.isAttachedToRecoveredSession = true;
    this.startAttemptTrace = null;
    this.windowIdResolved = false;
    this.uploadTokenResolved = false;

    this.subscribeToDesktopEvents({ associatedAudioRecordingId });
  }

  async start(abortSignal?: AbortSignal): Promise<void> {
    if (abortSignal?.aborted) return;
    if (!this.preparedRecording) {
      throw new Error("Desktop capture was not prepared");
    }
    if (!this.windowId || !this.uploadToken || !this.audioRecordingId) {
      throw new Error("Desktop capture not prepared");
    }
    logger.info({
      message: "[DesktopCaptureService] start requested",
      info: {
        audioRecordingId: this.audioRecordingId,
        recordingStrategy: this.recordingStrategy,
        detectedMeetingWindowId: this.detectedMeetingWindowId,
        windowIdResolved: this.windowIdResolved,
        uploadTokenResolved: this.uploadTokenResolved,
        ...buildDesktopRecordingStartAttemptLogInfo({
          startAttemptTrace: this.startAttemptTrace,
        }),
      },
    });
    this.callbacks.onDebugEvent?.({
      data: { windowId: this.windowId, detectedMeetingWindowId: this.detectedMeetingWindowId },
      kind: "recording-starting",
    });
    const result = await electronModule.recall.startRecording({
      audioRecordingId: this.audioRecordingId,
      windowId: this.windowId,
      uploadToken: this.uploadToken,
      recordingStrategy: this.recordingStrategy,
      calendarEventId: this.calendarEventId,
      startAttemptTrace: this.startAttemptTrace ?? undefined,
    });
    const outcome: StartRecordingOutcome = result;
    if (outcome.outcome === StartRecordingOutcomeKind.RestartRequired) {
      logger.warn({
        message: "[DesktopCaptureService] start returned restart required",
        info: {
          audioRecordingId: this.audioRecordingId,
          recordingStrategy: this.recordingStrategy,
          failureSource: DesktopRecordingStartFailureSource.RecallCoordinatorStart,
          failurePhase: DesktopRecordingStartFailurePhase.RecallStart,
          reason: outcome.reason,
          windowIdResolved: this.windowIdResolved,
          uploadTokenResolved: this.uploadTokenResolved,
          ...(outcome.errorDiagnostics ?? {}),
          ...buildDesktopRecordingStartAttemptLogInfo({
            startAttemptTrace: this.startAttemptTrace,
          }),
        },
      });
      throw new DesktopRecordingRestartRequiredError(outcome.reason, {
        failureSource: DesktopRecordingStartFailureSource.RecallCoordinatorStart,
        failurePhase: DesktopRecordingStartFailurePhase.RecallStart,
        startAttemptTrace: this.startAttemptTrace,
        noteId: null,
        detectedMeetingWindowId: this.detectedMeetingWindowId,
        recordingStrategy: this.recordingStrategy,
        windowIdResolved: this.windowIdResolved,
        uploadTokenResolved: this.uploadTokenResolved,
        recallSdkErrorDiagnostics: outcome.errorDiagnostics ?? null,
        restartSucceeded: true,
      });
    }
    if (outcome.outcome === StartRecordingOutcomeKind.Fatal) {
      logger.warn({
        message: "[DesktopCaptureService] start returned fatal",
        info: {
          audioRecordingId: this.audioRecordingId,
          recordingStrategy: this.recordingStrategy,
          failureSource: DesktopRecordingStartFailureSource.RecallCoordinatorRestart,
          failurePhase: DesktopRecordingStartFailurePhase.RecallRestart,
          error: outcome.error,
          windowIdResolved: this.windowIdResolved,
          uploadTokenResolved: this.uploadTokenResolved,
          ...(outcome.errorDiagnostics ?? {}),
          ...buildDesktopRecordingStartAttemptLogInfo({
            startAttemptTrace: this.startAttemptTrace,
          }),
        },
      });
      throw new DesktopRecordingStartFatalError(outcome.error, {
        failureSource: DesktopRecordingStartFailureSource.RecallCoordinatorRestart,
        failurePhase: DesktopRecordingStartFailurePhase.RecallRestart,
        startAttemptTrace: this.startAttemptTrace,
        noteId: null,
        detectedMeetingWindowId: this.detectedMeetingWindowId,
        recordingStrategy: this.recordingStrategy,
        windowIdResolved: this.windowIdResolved,
        uploadTokenResolved: this.uploadTokenResolved,
        recallSdkErrorMessage: outcome.error,
        recallSdkErrorDiagnostics: outcome.errorDiagnostics ?? null,
        restartSucceeded: false,
        restartError: outcome.error,
      });
    }
    if (abortSignal?.aborted) return;
    this.callbacks.onDebugEvent?.({
      data: { windowId: this.windowId, detectedMeetingWindowId: this.detectedMeetingWindowId },
      kind: "recording-started",
    });
  }

  async pause(): Promise<void> {
    await this.runSessionAction({
      directAction: ({ windowId }) => electronModule.recall.pause({ windowId }),
      recoveredSessionAction: ({ audioRecordingId }) =>
        electronModule.recall.pauseRecordingSession({
          audioRecordingId,
        }),
    });

    this.callbacks.onDebugEvent?.({
      data: {
        windowId: this.windowId,
        detectedMeetingWindowId: this.detectedMeetingWindowId,
        audioRecordingId: this.audioRecordingId,
      },
      kind: "recording-paused",
    });
  }

  async resume(): Promise<void> {
    await this.runSessionAction({
      directAction: ({ windowId }) => electronModule.recall.resume({ windowId }),
      recoveredSessionAction: ({ audioRecordingId }) =>
        electronModule.recall.resumeRecordingSession({
          audioRecordingId,
        }),
    });

    this.callbacks.onDebugEvent?.({
      data: {
        windowId: this.windowId,
        detectedMeetingWindowId: this.detectedMeetingWindowId,
        audioRecordingId: this.audioRecordingId,
      },
      kind: "recording-resumed",
    });
  }

  async stop(): Promise<void> {
    await this.ensureSdkStopped();
    this.callbacks.onDebugEvent?.({
      data: { windowId: this.windowId, detectedMeetingWindowId: this.detectedMeetingWindowId },
      kind: "recording-stopped",
    });
    this.cleanupAfterSession();
  }

  async cancel(): Promise<void> {
    await this.ensureSdkStopped();
    this.callbacks.onDebugEvent?.({
      data: { windowId: this.windowId, detectedMeetingWindowId: this.detectedMeetingWindowId },
      kind: "recording-cancelled",
    });
    this.cleanupAfterSession();
  }

  dispose() {
    if (this.isAttachedToRecoveredSession) {
      this.cleanupAfterSession();
      return;
    }

    void this.cancel();
  }

  private async ensureSdkStopped(): Promise<void> {
    if (!this.windowId && !this.audioRecordingId) {
      return;
    }
    if (this.stoppingPromise) {
      await this.stoppingPromise;
      return;
    }

    const windowId = this.windowId;
    const audioRecordingId = this.audioRecordingId;
    this.stoppingPromise = (async () => {
      try {
        logger.info({
          message: "[DesktopCaptureService] stopping",
          info: { windowId, audioRecordingId },
        });

        const stopOperation = this.runSessionAction({
          windowId,
          audioRecordingId,
          directAction: ({ windowId: activeWindowId }) =>
            electronModule.recall.stop({ windowId: activeWindowId }),
          recoveredSessionAction: ({ audioRecordingId: activeAudioRecordingId }) =>
            electronModule.recall.stopRecordingSession({
              audioRecordingId: activeAudioRecordingId,
            }),
        });
        const stopResult = await pTimeout<symbol | void>(stopOperation, {
          milliseconds: STOP_TIMEOUT_MS,
          fallback: () => STOP_TIMEOUT_SENTINEL,
        });
        if (stopResult === STOP_TIMEOUT_SENTINEL) {
          logger.warn({
            message: "[DesktopCaptureService] stop timed out",
            info: { windowId, audioRecordingId, timeoutMs: STOP_TIMEOUT_MS },
          });
        }
      } catch (err) {
        logger.warn({
          message: "[DesktopCaptureService] stop failed",
          info: { error: objectModule.safeErrorAsJson(err as Error) },
        });
      } finally {
        if (this.windowId === windowId) {
          this.windowId = null;
          this.activeWindowId = null;
        }
        this.uploadToken = null;
        this.audioRecordingId = null;
        this.isAttachedToRecoveredSession = false;
        this.stoppingPromise = null;
      }
    })();
    await this.stoppingPromise;
  }

  private async runSessionAction({
    windowId = this.windowId,
    audioRecordingId = this.audioRecordingId,
    directAction,
    recoveredSessionAction,
  }: SessionActionArgs & {
    directAction: (args: { windowId: string }) => Promise<void>;
    recoveredSessionAction: (args: { audioRecordingId: Uuid }) => Promise<void>;
  }): Promise<void> {
    if (windowId) {
      await directAction({ windowId });
      return;
    }

    if (!audioRecordingId) {
      return;
    }

    await recoveredSessionAction({ audioRecordingId });
  }

  private cleanupAfterSession() {
    try {
      this.unsubscribe?.();
    } catch (_) {
      /* empty */
    }
    try {
      this.waveformUnsubscribe?.();
    } catch (_) {
      /* empty */
    }
    this.unsubscribe = null;
    this.waveformUnsubscribe = null;
    this.resetTranscriptState();
    this.windowId = null;
    this.detectedMeetingWindowId = null;
    this.calendarEventId = undefined;
    this.uploadToken = null;
    this.activeWindowId = null;
    this.audioRecordingId = null;
    this.isAttachedToRecoveredSession = false;
    this.startAttemptTrace = null;
    this.windowIdResolved = false;
    this.uploadTokenResolved = false;
  }

  private subscribeToDesktopEvents({
    associatedAudioRecordingId,
  }: {
    associatedAudioRecordingId: Uuid;
  }): void {
    this.unsubscribe?.();
    this.waveformUnsubscribe?.();

    this.unsubscribe = subscribeDesktopRealtime(event => this.onEvent(event));
    this.waveformUnsubscribe = subscribeDesktopWaveformUpdates(({ audioRecordingId, update }) => {
      if (audioRecordingId !== associatedAudioRecordingId) {
        return;
      }

      this.callbacks.onWaveformUpdate?.({ update });
    });
  }

  private onEvent(parsedEvent: ParsedDesktopEvent) {
    if (!parsedEvent) return;

    const event = parsedEvent as ParsedTranscriptEvent | ParsedAudioEvent;
    if (event.type === "audio_mixed_raw.data") {
      // Recall AI includes the originating windowId, but we currently allow only one
      // recording at a time. Capture any audio payload the SDK emits.
      const samples = decodeBase64Pcm16({
        bufferBase64: event.bufferBase64,
      });
      this.callbacks.onAudioSamples?.({ samples });
      this.callbacks.onWaveformUpdate?.({
        update: {
          kind: "pcm16",
          sampleRate: DESKTOP_PCM_SAMPLE_RATE,
          samples,
          durationMs: undefined,
        },
      });
      return;
    }

    const channel: 0 | 1 = event.speakerIsHost ? 0 : 1; // host → Me (0), participants → Other Speakers (1)

    if (!event.endOfTurn) {
      const turnOrder = this.resolvePartialTurnOrder({ channel, event });
      if (turnOrder == null) {
        return;
      }

      const partialTurn = this.buildTranscriptTurn({
        event,
        turnOrder,
      }) as DesktopRecorderTranscriptPartialTurn;

      this.callbacks.onTranscriptPartial({
        channel,
        partialTurn,
      });
      return;
    }

    const finalTurn = this.buildTranscriptTurn({
      event,
      turnOrder: this.resolveFinalTurnOrder({ channel, event }),
    }) as DesktopRecorderTranscriptFinalTurn;

    this.callbacks.onTranscriptFinal({
      channel,
      finalTurn,
    });
  }

  private resetTranscriptState(): void {
    this.nextTranscriptTurnOrder = 0;
    this.activeTranscriptTurnOrdersBySpeaker.clear();
    this.lastFinalTranscriptTimestampBySpeaker.clear();
  }

  private buildTranscriptTurn({
    event,
    turnOrder,
  }: {
    event: ParsedTranscriptEvent;
    turnOrder: number;
  }): DesktopRecorderTranscriptTurn {
    return {
      turnOrder,
      transcript: event.transcript,
      timestampMs: event.timestamp,
      isFormatted: false,
      isEndOfTurn: event.endOfTurn,
      endOfTurnConfidence: event.endOfTurn ? 1 : 0,
      participantId: event.participantId ?? null,
      words: event.words.map(word => ({
        text: word.text,
        startMs: word.start,
        endMs: word.end,
        confidence: word.confidence,
      })),
      raw: event,
    };
  }

  private resolvePartialTurnOrder({
    channel,
    event,
  }: {
    channel: 0 | 1;
    event: ParsedTranscriptEvent;
  }): number | null {
    const speakerKey = this.buildSpeakerKey({ channel, event });
    const existingTurnOrder = this.activeTranscriptTurnOrdersBySpeaker.get(speakerKey);
    if (existingTurnOrder != null) {
      return existingTurnOrder;
    }

    const lastFinalTimestamp = this.lastFinalTranscriptTimestampBySpeaker.get(speakerKey);
    if (lastFinalTimestamp != null && event.timestamp <= lastFinalTimestamp) {
      return null;
    }

    const turnOrder = this.allocateTranscriptTurnOrder();
    this.activeTranscriptTurnOrdersBySpeaker.set(speakerKey, turnOrder);
    return turnOrder;
  }

  private resolveFinalTurnOrder({
    channel,
    event,
  }: {
    channel: 0 | 1;
    event: ParsedTranscriptEvent;
  }): number {
    const speakerKey = this.buildSpeakerKey({ channel, event });
    const existingTurnOrder = this.activeTranscriptTurnOrdersBySpeaker.get(speakerKey);
    this.lastFinalTranscriptTimestampBySpeaker.set(speakerKey, event.timestamp);

    if (existingTurnOrder != null) {
      this.activeTranscriptTurnOrdersBySpeaker.delete(speakerKey);
      return existingTurnOrder;
    }

    return this.allocateTranscriptTurnOrder();
  }

  private buildSpeakerKey({
    channel,
    event,
  }: {
    channel: 0 | 1;
    event: ParsedTranscriptEvent;
  }): string {
    if (event.participantId != null) {
      return `participant:${String(event.participantId)}`;
    }

    return `channel:${channel}`;
  }

  private allocateTranscriptTurnOrder(): number {
    const turnOrder = this.nextTranscriptTurnOrder;
    this.nextTranscriptTurnOrder += 1;
    return turnOrder;
  }
}
